import { jetbrainsMonoChartFontFamily } from '@/theme/utils/font.utils';
import { themeColors } from '@/theme/palette';
import type { CSSProperties } from 'react';

const TOOLTIP_BORDER_RADIUS = 6;
const TOOLTIP_BORDER_WIDTH = 1;
const TOOLTIP_BORDER_COLOR = themeColors.grey[700];
const TOOLTIP_BACKGROUND_COLOR = themeColors.background.secondary;
const TOOLTIP_PADDING_X = 14;
const TOOLTIP_PADDING_Y = 8;
const TOOLTIP_TITLE_SIZE = 12;
const TOOLTIP_TITLE_WEIGHT = 500;
const TOOLTIP_TITLE_COLOR = themeColors.text.primary;
const TOOLTIP_BODY_SIZE = 10;
const TOOLTIP_BODY_COLOR = themeColors.grey[400];
const TOOLTIP_FOOTER_SIZE = 10;
const TOOLTIP_FOOTER_COLOR = themeColors.text.primary;

export const PAGE_TOOLTIP_SHELL_STYLE: CSSProperties = {
  borderRadius: TOOLTIP_BORDER_RADIUS,
  borderWidth: TOOLTIP_BORDER_WIDTH,
  borderStyle: 'solid',
  borderColor: TOOLTIP_BORDER_COLOR,
  backgroundColor: TOOLTIP_BACKGROUND_COLOR,
  padding: `${TOOLTIP_PADDING_Y}px ${TOOLTIP_PADDING_X}px`,
  fontFamily: jetbrainsMonoChartFontFamily,
  boxShadow: 'none',
  boxSizing: 'border-box',
};

export const PAGE_TOOLTIP_SHELL_INLINE_STYLE = [
  `border-radius:${TOOLTIP_BORDER_RADIUS}px`,
  `border:${TOOLTIP_BORDER_WIDTH}px solid ${TOOLTIP_BORDER_COLOR}`,
  `background-color:${TOOLTIP_BACKGROUND_COLOR}`,
  `padding:${TOOLTIP_PADDING_Y}px ${TOOLTIP_PADDING_X}px`,
  `font-family:${jetbrainsMonoChartFontFamily}`,
  'box-shadow:none',
  'box-sizing:border-box',
].join(';');

export const PAGE_TOOLTIP_TITLE_MARGIN_BOTTOM = 6;
export const PAGE_TOOLTIP_BODY_LINE_GAP = 2;
export const PAGE_TOOLTIP_FOOTER_MARGIN_TOP = 6;

export const PAGE_TOOLTIP_TITLE_INLINE_CSS = [
  `margin:0 0 ${PAGE_TOOLTIP_TITLE_MARGIN_BOTTOM}px 0`,
  `font-size:${TOOLTIP_TITLE_SIZE}px`,
  `font-weight:${TOOLTIP_TITLE_WEIGHT}`,
  `color:${TOOLTIP_TITLE_COLOR}`,
  `font-family:${jetbrainsMonoChartFontFamily}`,
  'line-height:1.2',
].join(';');

/** Explanatory line under a tooltip title; wraps, unlike the figure lines. */
export const PAGE_TOOLTIP_SUBTITLE_INLINE_CSS = [
  `margin:0 0 ${PAGE_TOOLTIP_TITLE_MARGIN_BOTTOM}px 0`,
  `font-size:${TOOLTIP_BODY_SIZE}px`,
  'font-weight:400',
  `color:${TOOLTIP_BODY_COLOR}`,
  `font-family:${jetbrainsMonoChartFontFamily}`,
  'line-height:1.35',
  'max-width:200px',
  'white-space:normal',
].join(';');

export const PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS = [
  `font-size:${TOOLTIP_BODY_SIZE}px`,
  'font-style:italic',
  'font-weight:400',
  `color:${TOOLTIP_BODY_COLOR}`,
  `font-family:${jetbrainsMonoChartFontFamily}`,
  'line-height:1.2',
].join(';');

export const PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS = [
  `font-size:${TOOLTIP_FOOTER_SIZE}px`,
  'font-style:normal',
  'font-weight:400',
  `color:${TOOLTIP_FOOTER_COLOR}`,
  `font-family:${jetbrainsMonoChartFontFamily}`,
  'line-height:1.4',
].join(';');

export const PAGE_TOOLTIP_BODY_CONTAINER_INLINE_CSS = [
  'display:flex',
  'flex-direction:column',
  `gap:${PAGE_TOOLTIP_BODY_LINE_GAP}px`,
].join(';');

export const PAGE_TOOLTIP_FOOTER_CONTAINER_INLINE_CSS = [
  'display:flex',
  'flex-direction:column',
  `gap:${PAGE_TOOLTIP_BODY_LINE_GAP}px`,
  `margin-top:${PAGE_TOOLTIP_FOOTER_MARGIN_TOP}px`,
].join(';');

/**
 * Full-width rule between a tooltip's rows and its total. It spans every column of the
 * grid, so the gaps between columns do not break the line.
 */
export const PAGE_TOOLTIP_TOTAL_DIVIDER_INLINE_CSS = [
  'grid-column:1 / -1',
  'height:1px',
  `background:${TOOLTIP_BORDER_COLOR}`,
  `margin:${PAGE_TOOLTIP_FOOTER_MARGIN_TOP - PAGE_TOOLTIP_BODY_LINE_GAP}px 0`,
].join(';');

export function tooltipSwatchInlineStyle(color: string): string {
  return [
    'display:inline-block',
    'width:8px',
    'height:8px',
    'border-radius:1px',
    `background:${color}`,
    `border:2px solid ${color}`,
    'flex-shrink:0',
    'box-sizing:border-box',
  ].join(';');
}
